/**
 * App.jsx -- Port de main.js:bootstrap() + auth.js:tryResumeSession() +
 * shell.js (topbar + rutas). Decide, en este orden exacto (mismo orden
 * que el original, es importante para el anti-parpadeo de tema):
 *
 *   1. Tema de la APP (applyThemeFromBackend) -- cascada URL/localStorage/backend.
 *   2. Intentar reanudar sesion con el remember-token.
 *   3. Si NO hay sesion: tema INSTITUCIONAL de login (solo W2/B3, claro).
 *   4. Renderizar: sin sesion -> <LoginScreen>; con sesion -> el shell.
 *
 * `ready` reemplaza al #global-loader visible-por-defecto + hideGlobalLoader()
 * del original: mientras no este listo, <App> no renderiza nada dentro de
 * #root (el loader estatico de index.html sigue haciendo su trabajo).
 */
import { lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useSession } from './state/useSession.js';
import { api } from './lib/api.js';
import { getRememberToken, getSessionToken, setSession, clearSession, getSession } from './lib/sessionStore.js';
import { applyThemeFromBackend, applyLoginTheme, applyTheme, storeLoginTheme, splitTheme, LOGIN_BRAND_LABELS } from './theme/theme.js';
import { showGlobalLoader, hideGlobalLoader } from './lib/ui.js';
import AppLayout from './components/navigation/AppLayout.jsx';
import LoginScreen from './routes/LoginScreen.jsx';
import ModulesGrid from './routes/ModulesGrid.jsx';
import RequirePermission from './routes/RequirePermission.jsx';
import { MODULE_DEFS } from './routes/moduleDefs.js';

import RouteContent from './components/RouteContent.jsx';
import SessionRecovery from './components/SessionRecovery.jsx';
import { isInvalidSavedSession } from './lib/sessionRecovery.js';

const AccountView = lazy(() => import('./routes/AccountView.jsx'));
const RecordsView = lazy(() => import('./routes/records/RecordsView.jsx'));
const BigItemModule = lazy(() => import('./routes/records/BigItemModule.jsx'));
const DashboardView = lazy(() => import('./routes/dashboard/DashboardView.jsx'));
const IncidenciasView = lazy(() => import('./routes/records/IncidenciasView.jsx'));
const CatalogosView = lazy(() => import('./routes/catalogos/CatalogosView.jsx'));
const TiendasModule = lazy(() => import('./routes/tiendas/TiendasModule.jsx'));
const AdminSistemaView = lazy(() => import('./routes/admin/AdminSistemaView.jsx'));
const ConfigView = lazy(() => import('./routes/config/ConfigView.jsx'));
const DriverReportsModule = lazy(() => import('./routes/driverReports/DriverReportsModule.jsx'));
const DriverReportsListView = lazy(() => import('./routes/driverReports/DriverReportsListView.jsx'));
const DriverStatsView = lazy(() => import('./routes/driverReports/DriverStatsView.jsx'));
const NotificacionesView = lazy(() => import('./routes/notifications/NotificacionesView.jsx'));
const NotFoundView = lazy(() => import('./routes/NotFoundView.jsx'));

function useBootstrap(sessionToken) {
  const [ready, setReady] = useState(false);
  const [loginBrand, setLoginBrand] = useState('w2');
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const hadSessionRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      showGlobalLoader('Validando sesión...');
      try {
        const appTheme = await applyThemeFromBackend();
        const rememberToken = getRememberToken();
        if (rememberToken) {
          const result = await api.resume(rememberToken, 'INTERNO');
          if (result.ok) {
            setSession(result);
            applyTheme(result.temaPreferido);
            storeLoginTheme(result.temaLogin);
          } else if (isInvalidSavedSession(result)) {
            clearSession();
          } else if (!cancelled) {
            setRecoveryFailed(true);
          }
        }
        if (getSessionToken()) {
          const { brand } = splitTheme(getSession().temaPreferido || appTheme);
          if (!cancelled) setLoginBrand(brand);
        } else {
          const brand = await applyLoginTheme();
          if (!cancelled) setLoginBrand(brand);
        }
      } finally {
        hideGlobalLoader();
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cerrar sesion dentro de la SPA (menú de perfil, cambio de password) NO recarga
  // la pestaña -- a diferencia del 401 de api.js (clearSession + reload),
  // que ya vuelve a pasar por el efecto de arriba. Sin esto, <html
  // data-theme/data-color-scheme> se queda pegado en el tema personal con
  // el que se navegaba (incluido oscuro) y el login se pinta con esos
  // tokens en vez de los institucionales w2/b3 en claro.
  useEffect(() => {
    if (!ready) return;
    if (hadSessionRef.current && !sessionToken) {
      hadSessionRef.current = false;
      let cancelled = false;
      (async () => {
        const brand = await applyLoginTheme();
        if (!cancelled) setLoginBrand(brand);
      })();
      return () => { cancelled = true; };
    }
    hadSessionRef.current = !!sessionToken;
  }, [ready, sessionToken]);

  return { ready, loginBrand, recoveryFailed, setRecoveryFailed };
}

function moduleRoute(id, element) {
  const moduleDef = MODULE_DEFS.find((m) => m.id === id);
  return <RequirePermission moduleDef={moduleDef}>{element}</RequirePermission>;
}

function AppShell() {
  const { session, can } = useSession();
  const modules = MODULE_DEFS.filter(module => can(module.permiso));
  async function logout() {
    showGlobalLoader('Cerrando sesión...');
    try {
      if (session.token) await api.logout(session.token);
    } finally {
      clearSession();
      hideGlobalLoader();
    }
  }
  return (
    <AppLayout session={session} modules={modules} onLogout={logout}>
      <RouteContent>
        <Routes>
          <Route path="/" element={<ModulesGrid />} />
          <Route path="/mi-cuenta" element={<AccountView />} />
          <Route path="/pedidos" element={moduleRoute('bigitem', <BigItemModule />)}>
            <Route index element={<RecordsView />} />
            <Route path="estadisticas" element={<DashboardView />} />
            <Route path="incidencias" element={<IncidenciasView />} />
          </Route>
          <Route path="/catalogos" element={moduleRoute('catalogos', <CatalogosView />)} />
          <Route path="/tiendas" element={moduleRoute('tiendas', <TiendasModule />)} />
          <Route path="/admin-sistema" element={moduleRoute('adminsistema', <AdminSistemaView />)} />
          <Route path="/config" element={moduleRoute('config', <ConfigView />)} />
          <Route path="/reporte-drivers" element={moduleRoute('reportedrivers', <DriverReportsModule />)}>
            <Route index element={<DriverReportsListView />} />
            <Route path="estadisticas" element={<DriverStatsView />} />
          </Route>
          <Route path="/notificaciones" element={moduleRoute('notificaciones', <NotificacionesView />)} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </RouteContent>
    </AppLayout>
  );
}

export default function App() {
  const { session } = useSession();
  const { ready, loginBrand, recoveryFailed, setRecoveryFailed } = useBootstrap(session.token);

  if (!ready) return null;
  if (recoveryFailed && !session.token) return <SessionRecovery onSignIn={() => { clearSession(); setRecoveryFailed(false); }} />;
  if (!session.token) {
    return <LoginScreen institutionalBrandLabel={LOGIN_BRAND_LABELS[loginBrand] || LOGIN_BRAND_LABELS.w2} />;
  }
  return <AppShell />;
}
